/**
 * Rich-text descriptions (brand + manufacturer `descriptionRich`) and the plain
 * text that backs them.
 *
 * DUAL STORAGE: `descriptionRich` is TipTap HTML; `description` stays plain text
 * for app builds that predate the rich field. Every save that writes
 * `descriptionRich` writes `htmlToPlainText(descriptionRich)` to `description`
 * in the same write, so the legacy field never goes stale.
 *
 * `htmlToPlainText` is a port of watchlock's `src/utils/htmlToPlainText.ts`
 * (which the app uses to flatten rich bodies), so the plain text the admin
 * writes reads the same as what the app derives from the same HTML.
 */

const NAMED_ENTITIES: Record<string, string> = {
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&apos;': "'",
  '&#39;': "'",
  '&nbsp;': ' ',
  '&ndash;': '–',
  '&mdash;': '—',
  '&hellip;': '…',
  '&lsquo;': '‘',
  '&rsquo;': '’',
  '&ldquo;': '“',
  '&rdquo;': '”',
};

/** Block-level tags whose *close* ends a line. */
const BLOCK_CLOSE = /<\/(p|div|h[1-6]|li|tr|blockquote|pre)\s*>/gi;

/** Tags that are themselves a line break. */
const SELF_BREAK = /<(br|hr)\s*\/?>/gi;

/**
 * Elements whose text content is markup, not prose. Their *contents* have to go
 * with them — stripping only the tags would spill a stylesheet into the body.
 */
const NON_PROSE_ELEMENTS = /<(script|style|head|title)\b[^>]*>[\s\S]*?<\/\1\s*>/gi;

function decodeEntities(input: string): string {
  let out = input;
  for (const [entity, char] of Object.entries(NAMED_ENTITIES)) {
    out = out.split(entity).join(char);
  }
  // Numeric forms, decimal and hex. Out-of-range code points are left as source
  // text rather than throwing out of String.fromCodePoint.
  out = out.replace(/&#(\d+);/g, (match, digits: string) => {
    const code = parseInt(digits, 10);
    return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : match;
  });
  out = out.replace(/&#x([0-9a-f]+);/gi, (match, digits: string) => {
    const code = parseInt(digits, 16);
    return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : match;
  });
  // Last, so an escaped entity in the source stays escaped in the output.
  return out.split('&amp;').join('&');
}

/**
 * Convert `html` to plain text, preserving paragraph and list breaks.
 *
 * Returns an empty string for empty, whitespace-only, or tags-only input, so a
 * caller can treat the result as falsy and fall through to its next body
 * source rather than rendering an empty block.
 */
export function htmlToPlainText(html: string | undefined | null): string {
  if (!html) return '';

  let text = html;
  text = text.replace(NON_PROSE_ELEMENTS, '');
  // List items become bullets before the generic block-close rule turns their
  // closing tag into a newline.
  text = text.replace(/<li\b[^>]*>/gi, '• ');
  text = text.replace(SELF_BREAK, '\n');
  text = text.replace(BLOCK_CLOSE, '\n');
  text = text.replace(/<[^>]*>/g, '');
  text = decodeEntities(text);

  // Whitespace normalisation, in order: tabs and stray carriage returns to
  // spaces, runs of spaces collapsed, spaces around newlines trimmed, then runs
  // of three-or-more newlines capped at the blank line that separates
  // paragraphs. Doing this after the tag strip means markup indentation in the
  // source never reaches the reader.
  text = text.replace(/\r/g, '\n');
  text = text.replace(/[^\S\n]+/g, ' ');
  text = text.replace(/ *\n */g, '\n');
  text = text.replace(/\n{3,}/g, '\n\n');

  return text.trim();
}


const escapeHtml = (text: string): string =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * Legacy plain text → the editor's HTML: blank-line-separated blocks become
 * paragraphs, single line breaks inside one become `<br>`. Escaped, so text that
 * happens to contain `<` stays text. Empty in, empty out.
 */
export function plainTextToHtml(text: string | undefined | null): string {
  const trimmed = (text ?? '').replace(/\r\n?/g, '\n').trim();
  if (!trimmed) return '';
  return trimmed
    .split(/\n{2,}/)
    .map((block) => `<p>${escapeHtml(block.trim()).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

/** True when the editor holds nothing a reader would see (`<p></p>` and the like). */
export function isEmptyRichText(html: string | undefined | null): boolean {
  return htmlToPlainText(html) === '';
}

/**
 * The description fields to write when the rich editor CHANGED: `descriptionRich`
 * as HTML and `description` as its plain text, together. An emptied editor clears
 * both — `''` where a value was stored (the brand/manufacturer save paths turn
 * that into a clear), nothing where there was none.
 */
export function richDescriptionPatch(
  html: string,
  prev: { description?: string | null; descriptionRich?: string | null },
): { description?: string; descriptionRich?: string } {
  if (isEmptyRichText(html)) {
    return {
      ...(prev.descriptionRich ? { descriptionRich: '' } : {}),
      ...(prev.description ? { description: '' } : {}),
    };
  }
  return { descriptionRich: html, description: htmlToPlainText(html) };
}

/** The editor's starting HTML: the stored rich text, else the legacy plain text as paragraphs. */
export function richDescriptionSeed(entity: { description?: string | null; descriptionRich?: string | null }): string {
  return entity.descriptionRich?.trim() ? entity.descriptionRich : plainTextToHtml(entity.description);
}
